/**
 * useDragSelect Hook
 *
 * Enables rubber-band (lasso) drag selection over a grid of items.
 * The user clicks and drags on the container to draw a selection rectangle;
 * any child element whose `data-asset-id` attribute's bounding box intersects
 * the rectangle is considered selected.
 */

import { useRef, useState, useCallback, useEffect } from 'react';

export interface UseDragSelectOptions {
  /** Called during drag with the set of asset IDs currently within the rectangle (for visual feedback) */
  onSelectionChange?: (selectedIds: string[]) => void;
  /** Called once when drag ends with the final set of intersected IDs */
  onDragEnd?: (selectedIds: string[]) => void;
  /** Whether drag selection is enabled */
  enabled?: boolean;
}

export interface UseDragSelectResult {
  /** Ref to attach to the scrollable/grid container */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Whether the primary pointer is currently down on the drag surface */
  isPointerDown: boolean;
  /** Whether a drag is currently in progress */
  isDragging: boolean;
  /** Style object for the selection rectangle overlay */
  selectionRect: { top: number; left: number; width: number; height: number } | null;
}

/** Convert two viewport points to a rect relative to a container element */
function toContainerRect(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  containerRect: DOMRect,
) {
  return {
    left: Math.min(p1.x, p2.x) - containerRect.left,
    top: Math.min(p1.y, p2.y) - containerRect.top,
    width: Math.abs(p2.x - p1.x),
    height: Math.abs(p2.y - p1.y),
  };
}

/** Return asset IDs whose `data-asset-id` elements intersect the viewport rect defined by two points */
function computeIntersecting(
  container: HTMLElement,
  p1: { x: number; y: number },
  p2: { x: number; y: number },
): string[] {
  const selLeft = Math.min(p1.x, p2.x);
  const selTop = Math.min(p1.y, p2.y);
  const selRight = Math.max(p1.x, p2.x);
  const selBottom = Math.max(p1.y, p2.y);

  const cards = container.querySelectorAll<HTMLElement>('[data-asset-id]');
  const ids: string[] = [];

  cards.forEach((card) => {
    const rect = card.getBoundingClientRect();
    if (
      rect.right >= selLeft &&
      rect.left <= selRight &&
      rect.bottom >= selTop &&
      rect.top <= selBottom
    ) {
      const id = card.getAttribute('data-asset-id');
      if (id) ids.push(id);
    }
  });

  return ids;
}

/**
 * Hook that provides rubber-band drag selection behaviour.
 *
 * Attach `containerRef` to the grid wrapper. Each selectable child must have
 * a `data-asset-id="<id>"` attribute so the hook can identify which items
 * intersect the selection rectangle.
 *
 * Callbacks are stored in refs so they never cause event-listener
 * re-registration (avoids the stale-closure / listener-churn bugs).
 */
export function useDragSelect({
  onSelectionChange,
  onDragEnd,
  enabled = true,
}: UseDragSelectOptions): UseDragSelectResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectionRect, setSelectionRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  // --- stable refs for mutable data & callbacks ---
  const startPoint = useRef<{ x: number; y: number } | null>(null);
  const currentPoint = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);
  const rafId = useRef<number | null>(null);
  const lastIdsRef = useRef<string[]>([]);
  const previousBodyUserSelectRef = useRef<string>('');
  const previousBodyWebkitUserSelectRef = useRef<string>('');

  // Store callbacks in refs so event handlers always see the latest version
  // without needing to re-register listeners.
  const onSelectionChangeRef = useRef(onSelectionChange);
  const onDragEndRef = useRef(onDragEnd);
  useEffect(() => { onSelectionChangeRef.current = onSelectionChange; }, [onSelectionChange]);
  useEffect(() => { onDragEndRef.current = onDragEnd; }, [onDragEnd]);

  const disableNativeSelection = useCallback(() => {
    previousBodyUserSelectRef.current = document.body.style.userSelect;
    previousBodyWebkitUserSelectRef.current = document.body.style.webkitUserSelect;
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
  }, []);

  const restoreNativeSelection = useCallback(() => {
    document.body.style.userSelect = previousBodyUserSelectRef.current;
    document.body.style.webkitUserSelect = previousBodyWebkitUserSelectRef.current;
  }, []);

  const handleSelectStart = useCallback((e: Event) => {
    if (!startPoint.current) return;
    e.preventDefault();
  }, []);

  const handleNativeDragStart = useCallback((e: DragEvent) => {
    if (!startPoint.current) return;
    e.preventDefault();
  }, []);

  // --- event handlers (stable — no external deps that change) ---

  const handleMouseDown = useCallback((e: MouseEvent) => {
    // Only respond to primary button
    if (e.button !== 0) return;
    // Don't start drag on interactive child elements
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, select, textarea')) return;
    if (!containerRef.current) return;

    e.preventDefault();
    disableNativeSelection();
    window.getSelection()?.removeAllRanges();
    setIsPointerDown(true);
    startPoint.current = { x: e.clientX, y: e.clientY };
    currentPoint.current = { x: e.clientX, y: e.clientY };
    lastIdsRef.current = [];
  }, [disableNativeSelection]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!startPoint.current) return;

    e.preventDefault();
    currentPoint.current = { x: e.clientX, y: e.clientY };

    // Start visual drag only after a 5 px threshold to avoid interfering with clicks
    const dx = Math.abs(e.clientX - startPoint.current.x);
    const dy = Math.abs(e.clientY - startPoint.current.y);
    if (!isDraggingRef.current && dx < 5 && dy < 5) return;

    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
      setIsDragging(true);
    }

    // Throttle visual + intersection updates with rAF
    if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container || !startPoint.current || !currentPoint.current) return;

      const containerRect = container.getBoundingClientRect();
      setSelectionRect(toContainerRect(startPoint.current, currentPoint.current, containerRect));

      const ids = computeIntersecting(container, startPoint.current, currentPoint.current);
      lastIdsRef.current = ids;
      onSelectionChangeRef.current?.(ids);
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (rafId.current !== null) cancelAnimationFrame(rafId.current);

    // Fire onDragEnd only if a drag actually occurred
    if (isDraggingRef.current) {
      onDragEndRef.current?.(lastIdsRef.current);
    }

    startPoint.current = null;
    currentPoint.current = null;
    lastIdsRef.current = [];
    isDraggingRef.current = false;
    setIsPointerDown(false);
    setIsDragging(false);
    setSelectionRect(null);
    restoreNativeSelection();
  }, [restoreNativeSelection]);

  // Register listeners once (handlers are stable)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('selectstart', handleSelectStart, true);
    window.addEventListener('dragstart', handleNativeDragStart, true);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('selectstart', handleSelectStart, true);
      window.removeEventListener('dragstart', handleNativeDragStart, true);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
      restoreNativeSelection();
    };
  }, [enabled, handleMouseDown, handleMouseMove, handleMouseUp, handleNativeDragStart, handleSelectStart, restoreNativeSelection]);

  return { containerRef, isPointerDown, isDragging, selectionRect };
}
